import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CalendarRange, ChevronLeft, ChevronRight, Save, X, Pencil, ChevronDown, Check, Clock, MapPin, Users, Mic } from 'lucide-react';
import Navigation from '../components/Navigation';
import { getAdminHeaders, canEdit } from '../lib/auth';
import { getCurrentWeekNum } from '../lib/weekUtils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VolunteerDropdown = ({ field, label, editData, setEditData, volunteers }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = editData[field] || [];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (volId) => {
    setEditData(prev => {
      const arr = prev[field] || [];
      const updated = arr.includes(volId) ? arr.filter(id => id !== volId) : [...arr, volId];
      return { ...prev, [field]: updated };
    });
  };

  const selectedNames = selected
    .map(id => volunteers.find(v => v.id === id))
    .filter(Boolean)
    .map(v => v.nome.split(' ')[0]);

  return (
    <div ref={ref} className="relative" data-testid={`vol-dropdown-${field}`}>
      <label className="block text-xs font-semibold text-[#1A4D2E] mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-left hover:border-[#1A4D2E] transition-colors"
      >
        <span className="truncate flex-1">
          {selectedNames.length > 0 ? selectedNames.join(', ') : <span className="text-gray-400">Selecionar...</span>}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {selected.length > 0 && (
        <div className="text-[11px] text-[#1A4D2E] font-medium mt-0.5">{selected.length} selecionado(s)</div>
      )}
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl">
          {volunteers.length === 0 && <p className="text-xs text-gray-400 p-3">Nenhum voluntario</p>}
          {volunteers.map(v => {
            const isSelected = selected.includes(v.id);
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => toggle(v.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#F2F7F4] transition-colors ${isSelected ? 'bg-[#F2F7F4] text-[#1A4D2E] font-semibold' : 'text-gray-700'}`}
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-[#1A4D2E] border-[#1A4D2E]' : 'border-gray-300'}`}>
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </span>
                <span className="truncate">{v.nome}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AgendaMinisterio = () => {
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [weeks, setWeeks] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingWeek, setEditingWeek] = useState(null);
  const [editData, setEditData] = useState({});
  const [expandedWeek, setExpandedWeek] = useState(null);

  const MONTHS = ['Janeiro','Fevereiro','Marco','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

  // Mapeamento campo -> área do voluntário para filtragem
  const FIELD_AREA_MAP = {
    louvor_ids: 'louvor',
    midia_ids: 'midia',
    checkin_ids: 'checkin',
    sementes_ids: 'projeto_semente',
    intercessao_ids: 'intercessao',
    abertura_ids: null, // qualquer voluntário pode fazer abertura
  };

  const getFilteredVolunteers = (field) => {
    const area = FIELD_AREA_MAP[field];
    if (!area) return volunteers;
    return volunteers.filter(v => v.areas && v.areas.includes(area));
  };

  const filteredWeeks = weeks.filter(w => {
    if (!w.data) return false;
    const d = new Date(w.data + 'T12:00:00');
    return d.getMonth() === month;
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [agendaRes, volRes] = await Promise.all([
        axios.get(`${API}/agenda/${year}`, { headers: getAdminHeaders() }),
        axios.get(`${API}/volunteers`, { headers: getAdminHeaders() }),
      ]);
      setWeeks(agendaRes.data.weeks || []);
      setVolunteers(volRes.data || []);
    } catch (e) {
      toast.error('Erro ao carregar agenda');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEdit = (week) => {
    setEditingWeek(week.semana);
    setEditData({
      data: week.data || '',
      horario_inicio: week.horario_inicio || '',
      horario_fim: week.horario_fim || '',
      ambiente: week.ambiente || '',
      programacao: week.programacao || '',
      tema: week.tema || '',
      pregador: week.pregador || '',
      abertura_ids: week.abertura_ids || [],
      louvor_ids: week.louvor_ids || [],
      checkin_ids: week.checkin_ids || [],
      sementes_ids: week.sementes_ids || [],
      midia_ids: week.midia_ids || [],
      intercessao_ids: week.intercessao_ids || [],
      observacoes: week.observacoes || '',
    });
  };

  const cancelEdit = () => { setEditingWeek(null); setEditData({}); };

  const saveEdit = async (semana) => {
    try {
      await axios.put(`${API}/agenda/${year}/${semana}`, editData, { headers: getAdminHeaders() });
      toast.success(`Semana ${semana} atualizada!`);
      cancelEdit();
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr + 'T12:00:00');
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch { return dateStr; }
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr + 'T12:00:00');
      return d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
    } catch { return dateStr; }
  };

  const getVolNames = (ids) => {
    if (!ids || ids.length === 0) return '-';
    return ids.map(id => {
      const v = volunteers.find(vol => vol.id === id);
      return v ? v.nome.split(' ')[0] : '?';
    }).join(', ');
  };

  const currentWeekNum = getCurrentWeekNum();

  const RoleBadge = ({ label, names, color }) => {
    if (!names || names === '-') return null;
    return (
      <div className="flex items-start gap-1.5">
        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${color} whitespace-nowrap`}>{label}</span>
        <span className="text-xs text-gray-700">{names}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      {/* Header */}
      <div className="bg-[#1A4D2E] text-white px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="agenda-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <CalendarRange className="w-6 h-6 sm:w-7 sm:h-7" />
              <h1 className="font-nunito font-bold text-2xl sm:text-3xl">Agenda do Ministério</h1>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setYear(y => y - 1)} className="bg-white/10 hover:bg-white/20 rounded-full p-2" data-testid="agenda-prev-year">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="font-nunito font-bold text-xl min-w-[60px] text-center" data-testid="agenda-year">{year}</span>
              <button onClick={() => setYear(y => y + 1)} className="bg-white/10 hover:bg-white/20 rounded-full p-2" data-testid="agenda-next-year">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Month tabs */}
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="month-filter">
          {MONTHS.map((m, i) => (
            <button key={i} onClick={() => setMonth(i)} data-testid={`month-btn-${i}`}
              className={`px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                month === i ? 'bg-[#1A4D2E] text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#1A4D2E]'
              }`}
            >{m}</button>
          ))}
        </div>

        {loading ? (
          <p className="text-gray-500 text-center py-8" data-testid="agenda-loading">Carregando agenda...</p>
        ) : filteredWeeks.length === 0 ? (
          <p className="text-gray-400 text-center py-12">Nenhuma semana em {MONTHS[month]}</p>
        ) : (
          <>
            {/* MOBILE: Card layout */}
            <div className="lg:hidden space-y-3">
              {filteredWeeks.map(w => {
                const isCurrent = w.semana === currentWeekNum && year === new Date().getFullYear();
                const isExpanded = expandedWeek === w.semana;
                return (
                  <div key={w.semana} data-testid={`week-card-${w.semana}`}
                    className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all ${
                      isCurrent ? 'border-amber-400 ring-1 ring-amber-200' : 'border-gray-100'
                    }`}
                  >
                    <button onClick={() => setExpandedWeek(isExpanded ? null : w.semana)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left"
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm shrink-0 ${
                        isCurrent ? 'bg-amber-100 text-amber-800' : 'bg-[#E8F5ED] text-[#1A4D2E]'
                      }`}>S{w.semana}</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-800 capitalize">{formatDateFull(w.data)}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{w.horario_inicio || '16:30'} - {w.horario_fim || '18:00'}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{w.ambiente || 'Salao Esperanca'}</span>
                        </div>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-100 px-4 py-3 space-y-2 bg-gray-50/50">
                        {w.programacao && <div className="text-xs"><span className="font-semibold text-[#1A4D2E]">Programa:</span> {w.programacao}</div>}
                        {w.tema && <div className="text-xs"><span className="font-semibold text-[#1A4D2E]">Tema:</span> {w.tema}</div>}
                        {w.pregador && <div className="text-xs"><span className="font-semibold text-[#1A4D2E]">Pregador:</span> {w.pregador}</div>}

                        <div className="flex flex-wrap gap-1.5 pt-1">
                          <RoleBadge label="Abertura" names={getVolNames(w.abertura_ids)} color="bg-orange-100 text-orange-700" />
                          <RoleBadge label="Louvor" names={getVolNames(w.louvor_ids)} color="bg-blue-100 text-blue-700" />
                          <RoleBadge label="Checkin" names={getVolNames(w.checkin_ids)} color="bg-green-100 text-green-700" />
                          <RoleBadge label="Sementes" names={getVolNames(w.sementes_ids)} color="bg-amber-100 text-amber-700" />
                          <RoleBadge label="Mídia" names={getVolNames(w.midia_ids)} color="bg-purple-100 text-purple-700" />
                          <RoleBadge label="Intercessão" names={getVolNames(w.intercessao_ids)} color="bg-teal-100 text-teal-700" />
                        </div>

                        {w.observacoes && <div className="text-xs text-gray-500 italic pt-1">{w.observacoes}</div>}

                        {canEdit() && (
                          <button onClick={() => startEdit(w)} data-testid={`edit-week-${w.semana}`}
                            className="w-full mt-2 flex items-center justify-center gap-2 bg-[#1A4D2E] text-white rounded-lg py-2 text-sm font-semibold hover:bg-[#145A32] transition-all">
                            <Pencil className="w-3.5 h-3.5" /> Editar Semana
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* DESKTOP: Table layout */}
            <div className="hidden lg:block overflow-x-auto rounded-xl shadow-sm border border-gray-100">
              <table className="w-full text-sm border-collapse" data-testid="agenda-table">
                <thead>
                  <tr className="bg-[#1A4D2E] text-white">
                    <th className="px-2 py-2.5 text-left rounded-tl-xl font-nunito text-xs w-10">Sem</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs w-14">Data</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs w-12">Inicio</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs w-12">Fim</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs w-20">Ambiente</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs">Prog.</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs">Tema</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs">Abertura</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs">Pregador</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs">Louvor</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs">Checkin</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs">Sementes</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs">Mídia</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs">Intercess.</th>
                    <th className="px-2 py-2.5 text-left font-nunito text-xs">Obs</th>
                    <th className="px-2 py-2.5 text-center rounded-tr-xl font-nunito text-xs w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWeeks.map(w => {
                    const isCurrent = w.semana === currentWeekNum && year === new Date().getFullYear();
                    return (
                      <tr key={w.semana} data-testid={`week-row-${w.semana}`}
                        className={`border-b border-gray-100 hover:bg-[#F2F7F4]/50 transition-colors ${
                          isCurrent ? 'bg-amber-50 border-l-4 border-l-amber-400' : w.semana % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                        }`}
                      >
                        <td className="px-2 py-2 font-bold text-[#1A4D2E] text-center text-xs">{w.semana}</td>
                        <td className="px-2 py-2 text-xs whitespace-nowrap">{formatDate(w.data)}</td>
                        <td className="px-2 py-2 text-xs whitespace-nowrap">{w.horario_inicio || '-'}</td>
                        <td className="px-2 py-2 text-xs whitespace-nowrap">{w.horario_fim || '-'}</td>
                        <td className="px-2 py-2 text-xs text-gray-700">{w.ambiente || '-'}</td>
                        <td className="px-2 py-2 text-xs font-semibold text-[#1A4D2E] max-w-[100px] truncate">{w.programacao || '-'}</td>
                        <td className="px-2 py-2 text-xs max-w-[80px] truncate">{w.tema || '-'}</td>
                        <td className="px-2 py-2 text-xs text-orange-700">{getVolNames(w.abertura_ids)}</td>
                        <td className="px-2 py-2 text-xs">{w.pregador || '-'}</td>
                        <td className="px-2 py-2 text-xs text-blue-700">{getVolNames(w.louvor_ids)}</td>
                        <td className="px-2 py-2 text-xs text-green-700">{getVolNames(w.checkin_ids)}</td>
                        <td className="px-2 py-2 text-xs text-amber-700">{getVolNames(w.sementes_ids)}</td>
                        <td className="px-2 py-2 text-xs text-purple-700">{getVolNames(w.midia_ids)}</td>
                        <td className="px-2 py-2 text-xs text-teal-700">{getVolNames(w.intercessao_ids)}</td>
                        <td className="px-2 py-2 text-xs text-gray-500 max-w-[80px] truncate">{w.observacoes || '-'}</td>
                        <td className="px-2 py-2 text-center">
                          {canEdit() && (
                            <button data-testid={`edit-week-${w.semana}`} onClick={() => startEdit(w)}
                              className="text-gray-400 hover:text-[#1A4D2E] hover:bg-[#F2F7F4] rounded-full p-1 transition-all">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Edit Modal (works on both mobile and desktop) */}
      {editingWeek && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center" data-testid="edit-modal">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] flex flex-col shadow-2xl">
            <div className="bg-[#1A4D2E] text-white px-5 py-4 flex items-center justify-between rounded-t-2xl shrink-0">
              <h2 className="font-nunito font-bold text-lg">Editar Semana {editingWeek}</h2>
              <button onClick={cancelEdit} className="hover:bg-white/10 rounded-full p-1.5" data-testid="close-edit-modal">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {/* Date & Time */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[#1A4D2E] mb-1">Data</label>
                  <input type="date" value={editData.data} onChange={e => setEditData(p => ({...p, data: e.target.value}))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" data-testid="edit-data" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#1A4D2E] mb-1">Inicio</label>
                  <input type="time" value={editData.horario_inicio} onChange={e => setEditData(p => ({...p, horario_inicio: e.target.value}))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" data-testid="edit-horario-inicio" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#1A4D2E] mb-1">Fim</label>
                  <input type="time" value={editData.horario_fim} onChange={e => setEditData(p => ({...p, horario_fim: e.target.value}))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" data-testid="edit-horario-fim" />
                </div>
              </div>

              {/* Ambiente */}
              <div>
                <label className="block text-xs font-semibold text-[#1A4D2E] mb-1">Ambiente</label>
                <input value={editData.ambiente} onChange={e => setEditData(p => ({...p, ambiente: e.target.value}))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Ex: Salao Esperanca" data-testid="edit-ambiente" />
              </div>

              {/* Programa & Tema */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[#1A4D2E] mb-1">Programacao</label>
                  <input value={editData.programacao} onChange={e => setEditData(p => ({...p, programacao: e.target.value}))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Prog." data-testid="edit-programacao" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#1A4D2E] mb-1">Tema</label>
                  <input value={editData.tema} onChange={e => setEditData(p => ({...p, tema: e.target.value}))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Tema" data-testid="edit-tema" />
                </div>
              </div>

              {/* Pregador */}
              <div>
                <label className="block text-xs font-semibold text-[#1A4D2E] mb-1">Pregador</label>
                <div className="flex gap-2">
                  <select
                    value={volunteers.find(v => v.nome === editData.pregador) ? editData.pregador : '__custom'}
                    onChange={e => { if (e.target.value !== '__custom') setEditData(p => ({...p, pregador: e.target.value})); }}
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm" data-testid="edit-pregador-select"
                  >
                    <option value="__custom">Selecionar ou digitar...</option>
                    {volunteers.map(v => <option key={v.id} value={v.nome}>{v.nome}</option>)}
                  </select>
                </div>
                <input value={editData.pregador} onChange={e => setEditData(p => ({...p, pregador: e.target.value}))}
                  className="w-full mt-1 rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Ou digite o nome" data-testid="edit-pregador-input" />
              </div>

              {/* Volunteer Dropdowns - filtrados por área */}
              <div className="grid grid-cols-2 gap-3">
                <VolunteerDropdown field="abertura_ids" label="Abertura" editData={editData} setEditData={setEditData} volunteers={getFilteredVolunteers('abertura_ids')} />
                <VolunteerDropdown field="louvor_ids" label="Louvor" editData={editData} setEditData={setEditData} volunteers={getFilteredVolunteers('louvor_ids')} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <VolunteerDropdown field="checkin_ids" label="Checkin" editData={editData} setEditData={setEditData} volunteers={getFilteredVolunteers('checkin_ids')} />
                <VolunteerDropdown field="sementes_ids" label="Sementes" editData={editData} setEditData={setEditData} volunteers={getFilteredVolunteers('sementes_ids')} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <VolunteerDropdown field="midia_ids" label="Mídia" editData={editData} setEditData={setEditData} volunteers={getFilteredVolunteers('midia_ids')} />
                <VolunteerDropdown field="intercessao_ids" label="Intercessão" editData={editData} setEditData={setEditData} volunteers={getFilteredVolunteers('intercessao_ids')} />
              </div>

              {/* Observacoes */}
              <div>
                <label className="block text-xs font-semibold text-[#1A4D2E] mb-1">Observacoes</label>
                <textarea value={editData.observacoes} onChange={e => setEditData(p => ({...p, observacoes: e.target.value}))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" rows={2} placeholder="Observacoes..." data-testid="edit-observacoes" />
              </div>
            </div>

            {/* Sticky footer buttons */}
            <div className="shrink-0 border-t border-gray-200 bg-white px-5 py-4 flex gap-3 rounded-b-2xl">
              <button onClick={cancelEdit} className="flex-1 border border-gray-300 text-gray-600 rounded-xl py-3 font-semibold hover:bg-gray-50 transition-all" data-testid="cancel-edit">
                Cancelar
              </button>
              <button onClick={() => saveEdit(editingWeek)} data-testid="save-edit"
                className="flex-1 bg-[#1A4D2E] text-white rounded-xl py-3 font-semibold hover:bg-[#145A32] transition-all flex items-center justify-center gap-2">
                <Save className="w-4 h-4" /> Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      <Navigation active="inicio" />
    </div>
  );
};

export default AgendaMinisterio;
